import { ChangeEvent, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Draggable, Droppable } from '@atom/components/common/dragAndDrop';
import FontIconButton from '@atom/components/common/FontIconButton';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { Icon, Menu, Modal, TextField } from '@atom/mui';
import {
  AddIcon,
  ContentCopyIcon,
  DeleteIcon,
  DragIndicatorIcon,
  KeyboardArrowLeftIcon,
} from '@atom/mui/icons';
import { pageContainsModule } from '@atom/selectors/formModuleSelectors';
import colors from '@atom/styles/colors';
import { FormFieldPageType, FormModuleKeys } from '@atom/types/form';

import classes from './formBuilder.module.css';

const { MenuItem } = Menu;

interface Props {
  formTemplate: Object;
  toggleDrawer: () => void;
  updateSelectedPage: (index: number) => void;
  updatePageInEditMode: (index: any) => void;
  addPage: () => void;
  deletePage: (index: number) => boolean;
  duplicatePage: (index: number) => void;
  updatePageName: (name: string) => void;
  selectedPage: number;
  pageInEditMode: any;
  isDrawerOpen: boolean;
  pageName: string;
}

const DROPPABLE_ID = 'pages';

const FormPages = ({
  selectedPage,
  updateSelectedPage,
  updatePageName,
  deletePage,
  duplicatePage,
  updatePageInEditMode,
  pageInEditMode,
  pageName,
  formTemplate,
  isDrawerOpen,
  toggleDrawer,
  addPage,
}: Props) => {
  const [deleteModalOpenId, setDeleteModalOpenId] = useState<string>(null);
  const toggleDeleteModal = (pageIdToOpen: string | null) => {
    setDeleteModalOpenId(pageIdToOpen);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    updatePageName(event.target.value);
  };

  const handleSubmit = event => {
    if (event.key === 'Enter') {
      if (R.isEmpty(pageName)) {
        updatePageName('Untitled');
      }
      updatePageInEditMode(null);
    }
  };

  const onBlur = () => {
    if (R.isEmpty(pageName)) {
      updatePageName('Untitled');
    }

    updatePageInEditMode(null);
  };

  const isStandaloneModulePage = (page: FormFieldPageType): boolean => {
    const { order, fields } = page;

    if (R.isEmpty(order) || R.isEmpty(fields)) {
      return false;
    }

    // @ts-ignore
    return Object.values(FormModuleKeys).includes(fields[order[0]].type);
  };

  const buildPages = (pages: FormFieldPageType[]): any[] => {
    return pages.map((page: FormFieldPageType, index: number) => {
      const pageItemClassName = clsx(
        classes.pageListItem,
        selectedPage === index && classes.selected,
      );

      const isStandalone = isStandaloneModulePage(page);
      const isPciPage = pageContainsModule(FormModuleKeys.PCI, page);

      const isDeleteModalOpen = deleteModalOpenId === page.id;

      const onConfirmDeleteModal = () => {
        deletePage(index);
        toggleDeleteModal(null);
      };

      const menuOptions = (
        <>
          <Menu>
            {!isPciPage && (
              <MenuItem
                startAdornment={
                  <Icon>
                    <img src={renameIcon} />
                  </Icon>
                }
                onClick={() => updatePageInEditMode(index)}
              >
                Rename
              </MenuItem>
            )}
            {!isStandalone && (
              <MenuItem
                startAdornment={<Icon icon={ContentCopyIcon} />}
                onClick={() => duplicatePage(index)}
              >
                Duplicate
              </MenuItem>
            )}
            <MenuItem
              startAdornment={<Icon icon={DeleteIcon} />}
              onClick={() => toggleDeleteModal(page.id)}
            >
              Delete
            </MenuItem>
          </Menu>
          <Modal
            open={isDeleteModalOpen}
            title={`Delete ${page.name}`}
            onCancel={() => toggleDeleteModal(null)}
            confirmButtonText="Delete"
            ConfirmButtonProps={{
              sx: { background: colors.brand.red },
            }}
            onConfirm={onConfirmDeleteModal}
          >
            Are you sure you want to delete this page? All content will be
            deleted.
          </Modal>
        </>
      );

      return pageInEditMode !== index ? (
        <Draggable key={page.id} draggableId={page.id} index={index}>
          <div
            className={pageItemClassName}
            onClick={(): void => updateSelectedPage(index)}
          >
            <div className={classes.pageItemLeftContainer}>
              <Icon color={colors.neutral.ash} icon={DragIndicatorIcon} />
              <div className={classes.pageNameContainer}>{page.name}</div>
            </div>
            {menuOptions}
          </div>
        </Draggable>
      ) : (
        <div className={pageItemClassName} key={page.id}>
          <div className={classes.pageItemLeftContainer}>
            <Icon color={colors.neutral.ash} icon={DragIndicatorIcon} />
            <TextField
              variant="standard"
              autoFocus
              key={index}
              name="pageName"
              value={pageName}
              onBlur={onBlur}
              onChange={onChange}
              onKeyDown={handleSubmit}
            />
          </div>
          {menuOptions}
        </div>
      );
    });
  };

  const pages = R.pathOr([], ['pages'], formTemplate);
  const formPages = buildPages(pages);

  const containerStyle = {
    ...(!isDrawerOpen ? { display: 'none' } : {}),
  };

  return (
    <div className={classes.formPagesContainer} style={containerStyle}>
      <div className={classes.formPagesNavigation}>
        <div className={classes.addTabContainer}>
          <FontIconButton
            onClick={addPage}
            color={colors.neutral.gray}
            icon={AddIcon}
          />
          <div className={classes.addTabText}>add tab</div>
        </div>
        <FontIconButton
          onClick={toggleDrawer}
          color={colors.neutral.gray}
          icon={KeyboardArrowLeftIcon}
        />
      </div>
      <Droppable droppableId={DROPPABLE_ID}>{formPages}</Droppable>
    </div>
  );
};

export default FormPages;
